import React, { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import menuService from '../services/menuService'
import aiImageService, { type AiImageProvider } from '../services/aiImageService'
import config from '../services/config'
import { Menu, MenuItem } from '../types/menu'
import { useTranslation } from '../hooks/use-i18n'

interface CreateMenuWithAIModalProps {
  restaurantId: string
  /** When set, this menu is pre-selected and user goes straight to provider/items step */
  initialMenuId?: string
  onClose: () => void
  onSuccess: () => void
}

type Step = 'menu' | 'provider' | 'items' | 'payment' | 'generating'

const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 90000

function PaymentForm({
  clientSecret,
  amountCents,
  currency,
  onSuccess,
  onBack,
}: {
  clientSecret: string
  amountCents: number
  currency: string
  onSuccess: () => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [paying, setPaying] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    const cardElement = elements.getElement(CardElement)
    if (!cardElement) return
    setPaying(true)
    try {
      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      })
      if (error) {
        toast.error(error.message || t('toast.aiPaymentError'))
        return
      }
      onSuccess()
    } catch (err: any) {
      toast.error(err?.message || t('toast.aiPaymentError'))
    } finally {
      setPaying(false)
    }
  }

  const amountDisplay = (amountCents / 100).toFixed(2)
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-white/80 text-sm">
        {t('common.paymentTotal', { currency: currency.toUpperCase(), amount: amountDisplay })}
      </p>
      <div className="rounded-lg border border-white/20 bg-white/5 p-3">
        <CardElement
          options={{
            style: {
              base: {
                color: '#e5e7eb',
                fontFamily: 'system-ui, sans-serif',
                '::placeholder': { color: '#9ca3af' },
              },
              invalid: { color: '#f87171' },
            },
          }}
        />
      </div>
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
        >
          {t('common.back')}
        </button>
        <button
          type="submit"
          disabled={!stripe || paying}
          className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {paying ? t('common.processing') : t('common.payAndGenerate')}
        </button>
      </div>
    </form>
  )
}

const CreateMenuWithAIModal: React.FC<CreateMenuWithAIModalProps> = ({
  restaurantId,
  initialMenuId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('menu')
  const [menus, setMenus] = useState<Menu[]>([])
  const [providers, setProviders] = useState<AiImageProvider[]>([])
  const [selectedMenuId, setSelectedMenuId] = useState<string>('')
  const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null)
  const [selectedProviderId, setSelectedProviderId] = useState<string>('')
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentAmountCents, setPaymentAmountCents] = useState(0)
  const [paymentCurrency, setPaymentCurrency] = useState('usd')

  const stripePublishableKey = config.stripe?.publishableKey || ''
  const stripePromise = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey]
  )

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [menusData, providersData] = await Promise.all([
          menuService.getMenus(restaurantId),
          aiImageService.getProviders(restaurantId),
        ])
        setMenus(menusData)
        setProviders(providersData)
        if (initialMenuId && menusData.some((m) => (m.id || m._id) === initialMenuId)) {
          setSelectedMenuId(initialMenuId)
          setStep('items')
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('toast.aiLoadFailed'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [restaurantId, initialMenuId])

  useEffect(() => {
    if (!selectedMenuId) {
      setSelectedMenu(null)
      setSelectedItemIds([])
      return
    }
    const loadMenu = async () => {
      try {
        const menu = await menuService.getMenu(selectedMenuId)
        setSelectedMenu(menu)
        const items = menu.items || []
        setSelectedItemIds(items.map((i) => i.id || i._id!).filter(Boolean))
      } catch {
        setSelectedMenu(null)
      }
    }
    loadMenu()
  }, [selectedMenuId])

  const items: MenuItem[] = selectedMenu?.items || []
  const selectedProvider = providers.find((p) => p.id === selectedProviderId)
  const estimate =
    selectedProvider && selectedItemIds.length > 0
      ? selectedItemIds.length * selectedProvider.pricePerImage
      : 0

  const handlePayAndGenerate = async () => {
    if (!selectedMenuId || !selectedProviderId || selectedItemIds.length === 0) {
      toast.error(t('toast.aiSelectRequired'))
      return
    }
    if (!stripePublishableKey) {
      toast.error(t('toast.stripeNotConfigured'))
      return
    }
    try {
      setGenerating(true)
      const { clientSecret: secret, amountCents, currency } =
        await aiImageService.createAiImagePayment(restaurantId, selectedMenuId, {
          providerId: selectedProviderId,
          menuItemIds: selectedItemIds,
        })
      setClientSecret(secret)
      setPaymentAmountCents(amountCents)
      setPaymentCurrency(currency)
      setStep('payment')
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.aiPaymentFailed'))
    } finally {
      setGenerating(false)
    }
  }

  const handlePaymentSuccess = () => {
    setStep('generating')
    setGenerating(true)
  }

  useEffect(() => {
    if (step !== 'generating' || !selectedMenuId || !clientSecret) return
    const selectedSet = new Set(selectedItemIds)
    let cancelled = false
    const timeoutId = setTimeout(() => {
      cancelled = true
      setGenerating(false)
      toast.error(t('toast.aiGenerationTimeout'))
      setStep('items')
      setClientSecret(null)
    }, POLL_TIMEOUT_MS)

    const poll = async () => {
      if (cancelled) return
      try {
        const menu = await menuService.getMenu(selectedMenuId)
        const menuItems = menu.items || []
        const allHaveImages = selectedItemIds.every((id) => {
          const item = menuItems.find((i) => (i.id || i._id) === id)
          return item && item.aiGeneratedImageId
        })
        if (allHaveImages) {
          cancelled = true
          clearTimeout(timeoutId)
          setGenerating(false)
          setClientSecret(null)
          toast.success(t('toast.aiImagesGenerated'))
          onSuccess()
          onClose()
        }
      } catch {
        // ignore fetch errors, keep polling
      }
      if (!cancelled) setTimeout(poll, POLL_INTERVAL_MS)
    }
    poll()
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [step, selectedMenuId, clientSecret, selectedItemIds, onSuccess, onClose])

  const toggleItem = (id: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const selectAllItems = () => {
    const ids = items.map((i) => i.id || i._id!).filter(Boolean)
    setSelectedItemIds(ids)
  }

  const deselectAllItems = () => setSelectedItemIds([])

  const handlePaymentBack = () => {
    setStep('items')
    setClientSecret(null)
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500 mx-auto mb-4" />
          <p className="text-white/80">{t('menu.ai.loading')}</p>
        </div>
      </div>
    )
  }

  const menusWithItems = menus.filter((m) => m.items && m.items.length > 0)
  const enabledProviders = providers.filter((p) => p.enabled)

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 max-w-lg w-full my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">{t('menu.ai.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white p-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {step === 'generating' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto mb-4" />
            <p className="text-white font-medium">{t('menu.ai.paymentSuccessful')}</p>
            <p className="text-white/60 text-sm mt-2">
              {t('menu.ai.generatingWait')}
            </p>
          </div>
        )}

        {step === 'payment' && clientSecret && stripePromise && (
          <div className="mb-6">
            <label className="block text-white/80 text-sm font-medium mb-2">
              {t('menu.ai.enterCardDetails')}
            </label>
            <Elements stripe={stripePromise}>
              <PaymentForm
                clientSecret={clientSecret}
                amountCents={paymentAmountCents}
                currency={paymentCurrency}
                onSuccess={handlePaymentSuccess}
                onBack={handlePaymentBack}
              />
            </Elements>
          </div>
        )}

        {step !== 'generating' && step !== 'payment' && (
          <>
            {/* Step 1: Select menu */}
            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-2">
                {t('menu.ai.step1.label')}
              </label>
              {menusWithItems.length === 0 ? (
                <p className="text-white/60 text-sm">
                  {t('menu.ai.step1.empty')}
                </p>
              ) : (
                <select
                  value={selectedMenuId}
                  onChange={(e) => setSelectedMenuId(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
                >
                  <option value="">{t('common.chooseMenu')}</option>
                  {menusWithItems.map((m) => (
                    <option key={m.id || m._id} value={m.id || m._id}>
                      {m.name} ({(m.items || []).length} items)
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Step 2: Select provider */}
            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-2">
                {t('menu.ai.step2.label')}
              </label>
              {enabledProviders.length === 0 ? (
                <p className="text-white/60 text-sm">
                  {t('menu.ai.step2.empty')}
                </p>
              ) : (
                <div className="space-y-2">
                  {providers.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => p.enabled && setSelectedProviderId(p.id)}
                      disabled={!p.enabled}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                        selectedProviderId === p.id
                          ? 'border-sky-500 bg-sky-500/20 text-white'
                          : p.enabled
                            ? 'border-white/20 bg-white/5 hover:bg-white/10 text-white'
                            : 'border-white/10 bg-white/5 text-white/40 cursor-not-allowed'
                      }`}
                    >
                      <span className="font-medium">{p.name}</span>
                      <span className="text-sm text-white/70 block mt-0.5">
                        ${p.pricePerImage.toFixed(3)} per image
                        {p.description && ` · ${p.description}`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Step 3: Select items */}
            {selectedMenuId && items.length > 0 && (
              <div className="mb-6">
                <label className="block text-white/80 text-sm font-medium mb-2">
                {t('menu.ai.step3.label')}
                </label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={selectAllItems}
                    className="text-sky-400 hover:text-sky-300 text-sm font-medium"
                  >
                    {t('common.selectAll')}
                  </button>
                  <button
                    type="button"
                    onClick={deselectAllItems}
                    className="text-white/60 hover:text-white/80 text-sm font-medium"
                  >
                    {t('common.deselectAll')}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2 border border-white/10 rounded-lg p-2">
                  {items.map((item) => {
                    const id = item.id || item._id!
                    return (
                      <label
                        key={id}
                        className="flex items-center gap-2 cursor-pointer text-white/90 hover:text-white"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItemIds.includes(id)}
                          onChange={() => toggleItem(id)}
                          className="rounded border-white/30"
                        />
                        <span className="text-sm truncate">{item.name}</span>
                      </label>
                    )
                  })}
                </div>
                {selectedProvider && selectedItemIds.length > 0 && (
                  <p className="text-white/70 text-sm mt-2">
                    {t('common.approxTotal', { amount: estimate.toFixed(2), count: String(selectedItemIds.length), imageLabel: selectedItemIds.length !== 1 ? t('common.imagesPlural') : t('common.imageSingular') })}
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handlePayAndGenerate}
                disabled={
                  !selectedMenuId ||
                  !selectedProviderId ||
                  selectedItemIds.length === 0 ||
                  generating ||
                  !stripePublishableKey
                }
                className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? t('common.creatingPayment') : t('common.payAndGenerate')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CreateMenuWithAIModal
